import { useMemo } from 'react'

import { clsx } from 'clsx'

import {
  EventRecurrence,
  EventRegistrationStatus,
} from '@tribeplatform/gql-client/types'
import { Badge } from '@tribeplatform/react-ui-kit/Badge'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { dayjs } from '../../../common/lib/dayjs.js'
import { T } from '../../../i18n/index.js'
import { getEventFormattedDate } from '../../utils.js'

interface Props {
  recurrence: EventRecurrence
  isSelected: boolean
  isNextAvailable: boolean
}

export const RecurrenceItem = ({
  recurrence,
  isSelected,
  isNextAvailable,
}: Props) => {
  const { date, time } = getEventFormattedDate({
    startTime: recurrence.startTime,
    endTime: recurrence.endTime,
  })
  const location = recurrence.physicalLocations?.at(0)
  const isRegistered =
    recurrence.authMemberProps?.eventRegistration?.status ===
    EventRegistrationStatus.registered
  const isEnded = dayjs(recurrence.endTime).isBefore(dayjs())

  return (
    <div
      className={clsx(
        'flex gap-x-2 items-center p-3 border border-line rounded-base cursor-pointer transition-colors duration-300',
        isSelected && 'outline-action-primary outline',
        isEnded && 'opacity-70 hover:bg-surface-disabled',
        !isEnded && 'hover:bg-surface-subdued',
      )}
    >
      <DateBadge date={recurrence.startTime} />
      <div className="flex justify-between grow items-start">
        <div className="flex flex-col gap-y-1">
          <Text className="text-content-subdued">{date}</Text>
          <Text className="text-content-muted">{time}</Text>
          {location && (
            <Text className="text-content-muted flex items-center gap-x-1">
              <SvgIcon name="location-marker" />
              {location?.displayName}
            </Text>
          )}
        </div>
        <div className="flex flex-col gap-y-1">
          {isNextAvailable && (
            <Badge variant="highlight" color="primary" className="text-center">
              <T
                id="Event.Recurrence.Modal.NextAvailable"
                defaultMessage="Next available"
              />
            </Badge>
          )}
          {isRegistered && (
            <Badge variant="positive" color="primary" className="text-center">
              <T
                id="Event.Recurrence.Modal.Registered"
                defaultMessage="Registered"
              />
            </Badge>
          )}
          {isEnded && (
            <Badge variant="neutral" color="primary" className="text-center">
              <T
                id="Event.ActionButtons.PastEvent.HelperText"
                defaultMessage="Event has ended"
              />
            </Badge>
          )}
        </div>
      </div>
    </div>
  )
}

interface DateBadge {
  date: string
  className?: string
}

export const DateBadge = ({ date, className }: DateBadge) => {
  const { month, day } = useMemo(() => {
    const parsedDate = dayjs(date)
    return {
      month: parsedDate.format('MMM').toUpperCase(),
      day: parsedDate.format('D'),
    }
  }, [date])

  if (!date) {
    return null
  }

  return (
    <div
      className={clsx(
        'flex flex-col items-center justify-center',
        'aspect-square',
        'p-3',
        'bg-surface-subdued',
        'border border-line',
        'rounded-base',
        className,
      )}
    >
      <Text className="text-content-subdued font-semibold">{month}</Text>
      <Text className="text-content-muted">{day}</Text>
    </div>
  )
}

export const RecurrenceItemLoadingSkeleton = () => {
  return (
    <div className="animate-pulse flex gap-x-2 items-center p-3 border border-line rounded-base">
      {/* DateBadge skeleton */}
      <div className="flex flex-col items-center justify-center aspect-square p-3 bg-skeleton border border-line rounded-base">
        <div className="h-4 w-8 bg-surface-subdued rounded mb-1" />
        <div className="h-4 w-6 bg-surface-subdued rounded" />
      </div>
      {/* Content skeleton */}
      <div className="flex justify-between grow items-start">
        <div className="flex flex-col gap-y-1">
          <div className="h-4 bg-skeleton rounded w-32" />
          <div className="h-4 bg-skeleton rounded w-24" />
        </div>
      </div>
    </div>
  )
}
